import { ContentLanguage } from 'app/api/models/user/content-language';
import { salesProfileTagLabel } from 'app/modules/core/components/sales-profile-questionnaire/sales-profile-content';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { SalesProfilePlan } from 'app/api/services/sales-profile.service';

@Component({
  selector: 'app-sales-profile-summary-card',
  templateUrl: './sales-profile-summary-card.component.html',
  styleUrls: ['./sales-profile-summary-card.component.scss'],
  standalone: false,
})
export class SalesProfileSummaryCardComponent {
  /** AI-generated plan; null while loading or when generation failed. */
  @Input() plan: SalesProfilePlan | null = null;
  @Input() loading = false;
  /** Structured fallback shown when the plan couldn't be generated. */
  @Input() score: {
    track: string;
    trackLabel: string;
    focusAreas: string[];
  } | null = null;

  /**
   * The content language the plan was written for. The stored track is always
   * the Danish tag — it is the key the sprint matching runs on — so it is
   * translated for display only.
   */
  @Input() contentLanguage: ContentLanguage | null = null;

  /**
   * How this tenant names the personal-plan feature, resolved exactly the way
   * the sidebar resolves it: the tenant's custom title when set, otherwise the
   * feature key. Transloco passes an unknown key straight through, so a custom
   * title renders as written.
   */
  @Input() personalPlanTitleKey = 'personalplan';

  get trackLabel(): string {
    // Both sides carry the raw Danish tag, never a pre-formatted label: the
    // local score's trackLabel is already Danish text and could not be
    // translated back.
    const track = this.plan?.track ?? this.score?.track ?? '';
    return salesProfileTagLabel(track, this.contentLanguage);
  }

  /** Focus areas from the local score are raw tags; the saved plan is already localised. */
  focusAreaLabel(tag: string): string {
    return salesProfileTagLabel(tag, this.contentLanguage);
  }
  @Output() reset = new EventEmitter<void>();

  confirmOpen = false;

  readonly tileClass =
    'group flex items-center gap-3.5 w-full text-left px-4 py-3.5 rounded-xl border !border-slate-200/80 bg-white hover:!border-[var(--primary-color)] hover:bg-[color-mix(in_srgb,var(--primary-color)_4%,white)] hover:shadow-[0_10px_24px_-14px_rgba(15,23,42,.35)] transition-all';

  readonly tileIconClass =
    'shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-[color-mix(in_srgb,var(--primary-color)_10%,white)] text-[var(--primary-color)]';

  readonly tileEyebrowClass =
    'block text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400';

  readonly tileTitleClass =
    'block text-[15px] font-semibold text-slate-900 truncate';

  readonly tileChevronClass =
    'ml-auto shrink-0 text-slate-300 group-hover:text-[var(--primary-color)] transition-colors';

  // The tile opens the sprint/onboarding "main page" (the preview/overview with
  // the Start button), not the module viewer. Both kinds use /sprints/preview/:id
  // discriminated by the ?type query param.
  sprintLink(): string[] {
    return ['/sprints', 'preview', this.plan?.sprintId ?? ''];
  }

  get sprintTypeParam(): string {
    return this.plan?.sprintKind === 'Onboarding' ? 'Onboarding' : 'Sprint';
  }

  focusAreaLink(area: SalesProfilePlan['focusAreas'][number]): string[] {
    return area.focusAreaId
      ? ['/personalplan', area.focusAreaId]
      : ['/personalplan'];
  }

  onResetClick(): void {
    this.confirmOpen = true;
  }

  onConfirmCancel(): void {
    this.confirmOpen = false;
  }

  onConfirmReset(): void {
    this.confirmOpen = false;
    this.reset.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.confirmOpen) {
      this.onConfirmCancel();
    }
  }
}
